import type { RouterInputs } from '@client/trpc/client';
import { DateTime } from 'luxon';
import { z } from 'zod';

/**
 * URL contract of Claims → Lodgements (CUR-1408). Every facet lives in the route search so the
 * summary cards on a product version or a link, and the links table, can deep-link with the
 * filters preset: `?productId=…&productVersionId=…&linkId=…&preset=NEEDS_ATTENTION`.
 *
 * Ids travel as decimal strings (search params are JSON; bigint is not), dates as `YYYY-MM-DD`.
 */

export const LODGEMENT_PRESETS = ['ALL', 'NEEDS_ATTENTION', 'DRAFTS'] as const;
export type LodgementPreset = (typeof LODGEMENT_PRESETS)[number];

export const LODGEMENT_PRESET_OPTIONS: { value: LodgementPreset; label: string }[] = [
    { value: 'ALL', label: 'All' },
    { value: 'NEEDS_ATTENTION', label: 'Needs attention' },
    { value: 'DRAFTS', label: 'Drafts' },
];

/** The default "Last 30 days" window on the started-at column. */
export const LODGEMENT_DEFAULT_RANGE_DAYS = 30;

/**
 * A decimal id. The router's default search parser JSON-parses each value, so a hand-built
 * `?productId=12` arrives as the number 12 while a router-serialised link arrives as `'12'`; both
 * normalise to the digit string. Numbers are only trusted within the safe-integer range, where the
 * parse was lossless.
 */
const bigintString = z
    .union([z.string(), z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER)])
    .transform(String)
    .pipe(z.string().regex(/^\d{1,19}$/));
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const lodgementsSearchSchema = z.object({
    preset: z.enum(LODGEMENT_PRESETS).optional().catch(undefined),
    productId: bigintString.optional().catch(undefined),
    productVersionId: bigintString.optional().catch(undefined),
    linkId: bigintString.optional().catch(undefined),
    search: z.string().max(200).optional().catch(undefined),
    from: isoDay.optional().catch(undefined),
    to: isoDay.optional().catch(undefined),
});
export type LodgementsSearch = z.infer<typeof lodgementsSearchSchema>;

export type LodgementListInput = RouterInputs['lodgementSubmission']['listSubmissions'];
export type LodgementListPaging = Pick<LodgementListInput, 'limit' | 'offset' | 'orderBy'>;

export function toIsoDay(date: Date): string {
    return DateTime.fromJSDate(date).toISODate() ?? '';
}

/** `[from, to]` as dates for the range picker: the explicit window, else the default 30 days. */
export function resolveRange(search: Pick<LodgementsSearch, 'from' | 'to'>, now = new Date()): [Date, Date] {
    const today = DateTime.fromJSDate(now).startOf('day');
    const from = search.from
        ? DateTime.fromISO(search.from).startOf('day')
        : today.minus({ days: LODGEMENT_DEFAULT_RANGE_DAYS });
    const to = search.to ? DateTime.fromISO(search.to).endOf('day') : today.endOf('day');
    return [from.toJSDate(), to.toJSDate()];
}

/** Whether the caller set an explicit window (as opposed to the default 30 days). */
export function hasExplicitRange(search: Pick<LodgementsSearch, 'from' | 'to'>): boolean {
    return search.from !== undefined || search.to !== undefined;
}

/**
 * The list input for a search + paging state. The started-at window applies to the ALL preset
 * (default 30 days) and to any preset when the caller set one explicitly; the failed queue and
 * open drafts are otherwise not date-limited, so nothing needing attention hides behind a window.
 */
export function toListInput(
    search: LodgementsSearch,
    paging: LodgementListPaging,
    now = new Date(),
): LodgementListInput {
    const preset = search.preset ?? 'ALL';
    const applyRange = preset === 'ALL' || hasExplicitRange(search);
    const [from, to] = resolveRange(search, now);
    return {
        ...paging,
        preset,
        ...(search.search ? { search: search.search } : {}),
        ...(search.productId ? { productId: BigInt(search.productId) } : {}),
        ...(search.productVersionId ? { productVersionId: BigInt(search.productVersionId) } : {}),
        ...(search.linkId ? { linkId: BigInt(search.linkId) } : {}),
        ...(applyRange ? { from, to } : {}),
    };
}

/** Search for a deep link into the page with facets preset (ids as strings, empty facets dropped). */
export function lodgementsSearchFor(input: {
    preset?: LodgementPreset;
    productId?: bigint | null;
    productVersionId?: bigint | null;
    linkId?: bigint | null;
}): LodgementsSearch {
    return {
        ...(input.preset && input.preset !== 'ALL' ? { preset: input.preset } : {}),
        ...(input.productId ? { productId: input.productId.toString() } : {}),
        ...(input.productVersionId ? { productVersionId: input.productVersionId.toString() } : {}),
        ...(input.linkId ? { linkId: input.linkId.toString() } : {}),
    };
}
